import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { KEY_LIGHT_INTENSITY } from '../../common/constants/KeyLightIntensity'
import { OcppDimmerState } from '../../common/constants/OcppDimmerStateEnum'
import { ChargePoint, useOcppCommandUpdateConfigKeyMutation } from '../../common/redux/api/web-api'
import { getDimmerValue } from '../../common/utils/chargePointUtils'
import Text from '../../ui/Text'
import { IConfigKey } from '../loadOcppConfigKeys'
import { RemoteControlRow } from '../remote-control-row'
import setOcppConfigValue from '../setOcppConfigValue'
import isOnline from '../isOnline'
import useText from '../../common/hooks/useText'
import { useToast } from '../../common/uikit/ToastProvider'

export const SDimmerButton = styled.button<{ isOn: boolean }>`
    display: inline-block;
    border: 1px solid transparent;
    padding: 4px 15px;
    font-size: 14px;
    border-radius: 3px;
    text-transform: uppercase;
    color: #ffffff;
    background-color: ${({ isOn, theme }) => (isOn ? theme.font.successColor : theme.font.errorColor)};
    border: none;
    border: 1px solid ${({ theme }) => theme.brandColors.CA_Mid_Gray};
`

export type RemoteOcppDimmerControlProps = {
    chargePoint: ChargePoint
    isReadOnly: boolean
}

export function RemoteOcppDimmerControl({ chargePoint, isReadOnly }: RemoteOcppDimmerControlProps): React.ReactElement {
    const text = useText()
    const [configKeys, setConfigKeys] = useState<IConfigKey[]>(chargePoint?.ocppConfigKeys)
    const [dimmerValue, setDimmerValue] = useState(getDimmerValue(chargePoint?.ocppConfigKeys))

    const [saveConfigKey] = useOcppCommandUpdateConfigKeyMutation()

    const toast = useToast()

    useEffect(() => {
        setConfigKeys(chargePoint?.ocppConfigKeys)
    }, [chargePoint])

    useEffect(() => {
        setDimmerValue(getDimmerValue(configKeys))
    }, [configKeys])

    const getDimmerValueText = () => {
        if (dimmerValue === OcppDimmerState.OFF) {
            return text('lblOff')
        } else if (dimmerValue === OcppDimmerState.LOW) {
            return text('lblDimmerValueLow')
        } else if (dimmerValue === OcppDimmerState.MEDIUM) {
            return text('lblDimmerValueMed')
        } else if (dimmerValue === OcppDimmerState.HIGH) {
            return text('lblDimmerValueHigh')
        } else {
            return text('btnDimmerValueCustom')
        }
    }

    const changeDimmer = async () => {
        let newDimmerValue
        if (+dimmerValue === +OcppDimmerState.OFF) {
            newDimmerValue = OcppDimmerState.LOW
        } else if (+dimmerValue <= +OcppDimmerState.LOW) {
            newDimmerValue = OcppDimmerState.MEDIUM
        } else if (+dimmerValue <= +OcppDimmerState.MEDIUM) {
            newDimmerValue = OcppDimmerState.HIGH
        } else {
            newDimmerValue = OcppDimmerState.OFF
        }

        const updatedConfigKeys = setOcppConfigValue(KEY_LIGHT_INTENSITY, newDimmerValue, configKeys)
        if (!updatedConfigKeys) return

        await saveConfigKey({
            chargePointId: chargePoint.id,
            keyValue: updatedConfigKeys.find((configKey) => configKey.key === KEY_LIGHT_INTENSITY),
        })
            .unwrap()
            .then(() => setConfigKeys(updatedConfigKeys))
            .catch(() => toast.notifyError(text('msgRemoteControlCommandFailed')))
    }

    return (
        <RemoteControlRow
            button={
                <SDimmerButton
                    isOn={dimmerValue !== OcppDimmerState.OFF}
                    onClick={changeDimmer}
                    disabled={isReadOnly || !isOnline(chargePoint) || chargePoint?.ocppEnabled}
                    data-testid={`dimmer-control-${chargePoint.id}`}
                >
                    {getDimmerValueText()}
                </SDimmerButton>
            }
            title={<Text code="lblLEDBrightness" />}
        />
    )
}
